import { Component, inject, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { startWith, Subject, takeUntil } from 'rxjs';
import { IUser, MenuItem, MenuStructure, SubMenuItem } from '../../shared/interfaces/interface';
import { AuthService } from 'src/app/services/auth.service';
import { MenuConfigurationService } from 'src/app/services/shared/menu-configuration/menu-configuration.service';
import { MenuConfig } from './constants/menu-route-config';
import { UserAuthService } from 'src/app/services/user-auth.service';
import { AdminService } from '@modules/admin/services/admin.service';
import { DeviceService, DeviceType } from '@services/shared/device.service';
import {
  EmailURLConfigurationResponse,
  MenuConfiguration,
} from '@modules/admin/interfaces/admin-url.interface';
import { TooltipModule } from 'primeng/tooltip';
import { sortMenu } from '@services/utility/ors-table-utility';
import { CatCounterService } from '@services/cat-counter.service';
import { INITIAL_USER } from '@core/constants/app.constants';

@Component({
  selector: 'app-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  standalone: true,
  imports: [CommonModule, TooltipModule],
})
export class SidenavComponent implements OnInit {
  navigationList!: MenuItem[];
  selectedNavIcon: SubMenuItem | null = null;
  subNavShowIndex = 0;
  private documentClickHandler: (e: Event) => void | null = null;
  // no hover suppression — submenu visibility controlled by clicks only
  hideSideNav = false;
  showCloseBtn = false;
  private readonly destroy$ = new Subject();
  menuService = inject(MenuConfigurationService);
  userAuthSvc = inject(UserAuthService);
  adminService = inject(AdminService);
  deviceService = inject(DeviceService);
  isFirstLogin = false;
  isMobile = false;
  selectedMainNav: MenuItem | null = null;
  isAdmin = false;
  user: IUser = INITIAL_USER;
  menuConfigSubscription;

  constructor(
    private readonly router: Router,
    private readonly authSvc: AuthService,
    private readonly userSvc: UserAuthService,
    private readonly catCounterService: CatCounterService,
  ) {
    this.authSvc.isAdmin$.pipe(takeUntil(this.destroy$)).subscribe((isAdmin) => {
      this.isAdmin = isAdmin;
    });
  }

  ngOnInit(): void {
    this.deviceService.deviceType$.subscribe((deviceType) => {
      this.isMobile = deviceType === DeviceType.Mobile;
    });

    this.userAuthSvc.user$.subscribe((isReady) => {
      this.isFirstLogin = isReady.firstLogin;
    });

    this.menuConfigSubscription = this.adminService.menuConfigUpdated$
      .pipe(
        startWith('init'),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.fetchAdminMenuConfig());

    // Listen for route changes
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event instanceof NavigationEnd && this.navigationList && this.navigationList.length > 0) {
        this.setSelectedNavFromRoute();
      }
    });

    // Close submenu when clicking/touching outside the sidebar
    this.documentClickHandler = (e: Event) => {
      try {
        const sidebar = document.getElementById('sidebar');
        if (!sidebar) {
          return;
        }
        const target = e.target as Node;
        if (this.subNavShowIndex !== 0 && !sidebar.contains(target)) {
          this.subNavShowIndex = 0;
        }
      } catch (err) {
        // swallow errors in environments without a DOM
      }
    };
    document.addEventListener('click', this.documentClickHandler);
    document.addEventListener('touchstart', this.documentClickHandler);
  }

  fetchAdminMenuConfig() {
    this.menuService.getAppConfiguration().subscribe((data: MenuStructure) => {
      const emailUrlConfig: EmailURLConfigurationResponse = {
        urls: data.urls,
        emails: data.emails,
      };
      const menuConfig: MenuConfiguration = {
        menuItems: data.menuItems,
      };
      this.adminService.setAdminEmailsandUrls(emailUrlConfig);
      const sortedMenuItems = sortMenu(menuConfig.menuItems);
      this.adminService.setMenuItems(menuConfig);
      this.navigationList = sortedMenuItems.map((navItem) => ({
        ...navItem,
        iconName: MenuConfig[navItem.name]?.iconName || 'default-icon',
      }));
      this.setSelectedNavFromRoute();
    });
  }

  navigateTo(menuName): void {
    this.selectedNavIcon = menuName;

    const parent = this.navigationList?.find((nav) =>
      nav.subMenuItems.some((sub) => sub.name === menuName.name),
    );
    if (parent) {
      this.selectedMainNav = parent; // Track parent of submenu
    }

    const route =
      Object.values(MenuConfig)?.find((item) => item.subMenus?.[menuName.name]?.route)?.subMenus?.[
        menuName.name
      ]?.route ?? null;

    if (route) {
      this.router.navigate([route]);
      this.hideSideNav = false; // Close the side navigation after navigation
    } else {
      console.error(`Route not found for menu: ${menuName.name}`);
    }
  }

  
  toggleSideNav(): void {
    this.hideSideNav = !this.hideSideNav;
  }

  isDisabled(subNavName: SubMenuItem): {
    visuallyDisabled: boolean;
    tooltip: string;
    cursor: string;
    canNavigate: boolean;
  } {
    const isInactive = subNavName.isActive === 0;
    if (isInactive) {
      return {
        visuallyDisabled: true, // always show as disabled
        tooltip: 'Disabled by Administrator',
        cursor: this.isAdmin ? 'pointer' : 'not-allowed',
        canNavigate: this.isAdmin, // only admin can navigate
      };
    }
    return {
      visuallyDisabled: false,
      tooltip: '',
      cursor: 'pointer',
      canNavigate: true,
    };
  }

  navigateToParent(navList: MenuItem, index?: number): void {
    const parentConfig = MenuConfig[navList.name];

    const hasSubmenu = navList.subMenuItems?.length > 0;
    const hasRoute = !!parentConfig?.route;

    // If the item has submenus but no direct route, only open the submenu
    // and do not change the highlighted `selectedMainNav`.
    if (hasSubmenu && !hasRoute) {
      const foundIndex = typeof index === 'number'
        ? index
        : (this.navigationList?.findIndex((item) => item.name === navList.name) ?? -1);
      this.subNavShowIndex = foundIndex >= 0 ? foundIndex + 1 : 0;
      return;
    }

    // If the item has a route (with or without submenus), select it and navigate.
    this.selectedMainNav = navList;
    if (hasRoute) {
      this.router.navigate([parentConfig.route]);
      this.hideSideNav = false;
    }
  }

  handleNavInteraction(navList: MenuItem, index: number): void {
    // Click always opens/toggles submenus for items without routes.
    const hasSubmenu = navList.subMenuItems?.length > 0;
    const parentConfig = MenuConfig[navList.name];
    const hasRoute = !!parentConfig?.route;

    // If the top-level has submenus and no direct route, clicking should
    // reliably toggle/open the submenu regardless of earlier hover suppression.
    if (hasSubmenu && !hasRoute) {
      const oneBased = index + 1;
      // toggle if already open
      this.subNavShowIndex = this.subNavShowIndex === oneBased ? 0 : oneBased;
      return;
    }

    // Otherwise delegate to navigateToParent (which handles route navigation)
    this.navigateToParent(navList, index);
  }


  setSelectedNavFromRoute(): void {
    if (!this.navigationList || this.navigationList.length === 0) {
      return; // Guard against undefined
    }

    const currentUrl = this.router.url;

    // Match top-level route
    const matchedMain = this.navigationList?.find(
      (nav) => MenuConfig[nav.name]?.route === currentUrl,
    );
    if (matchedMain) {
      this.selectedMainNav = matchedMain;
      return;
    }

    // Match submenu route
    for (const nav of this.navigationList) {
      const subMenus = MenuConfig[nav.name]?.subMenus || {};
      for (const subName in subMenus) {
        const subRoute = subMenus[subName]?.route;
        if (subRoute && currentUrl.startsWith(subRoute)) {
          this.selectedMainNav = nav;
          this.selectedNavIcon = nav.subMenuItems?.find((sub) => sub.name === subName);
          return;
        }
      }
    }

    // If no match found, keep the previous selection so the
    // highlighted top-level nav remains until user explicitly
    // selects another menu item.
  }

  ngOnDestroy(){
    // notify all `takeUntil(this.destroy$)` subscriptions
    this.destroy$.next(null);
    this.destroy$.complete();

    if (this.menuConfigSubscription) {
      this.menuConfigSubscription.unsubscribe();
    }
    if (this.documentClickHandler) {
      document.removeEventListener('click', this.documentClickHandler);
      document.removeEventListener('touchstart', this.documentClickHandler);
      this.documentClickHandler = null;
    }
  }
}
